import { FC, useMemo, useState, useCallback, KeyboardEvent } from 'react';
import {
	Box,
	FormControl,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Theme,
	useMediaQuery,
} from '@mui/material';
import { useRouter } from 'next/router';
import Image from "next/legacy/image";
import ImageWrapper from '../../images/Image-wrapper';
import searchIcon from '../../../public/assets/icons/search-icon-black.svg';
import { heroScreenSearchBarStyles } from './hero-screen-search-bar.styles';
import { useStyles } from '../../../utils/hooks/useStyles';
import useLink from '../../../utils/hooks/useLink';
import { useLocation } from '../../../contexts/locationContext';
import { LocationOptionsMap } from '../../../constants';

const HeroScreenSearchBar: FC = () => {
	const router = useRouter();
	const { makeSearchEventsLink } = useLink();
	const { locationPath, locationCityId, updateLocation } = useLocation();

	const [search, setSearch] = useState<string>('');

	const locations = useMemo(
		() =>
			Object.values(LocationOptionsMap).map(location => ({
				id: location.cityId,
				name: location.name,
			})),
		[]
	);

	const handleLocationChange = (event: SelectChangeEvent) => {
		updateLocation(event.target.value as string);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	};

	const onSearchHandler = useCallback(
		(e) => {
			router.push(makeSearchEventsLink({ search, place: locationPath }));
		},
		[locationPath, makeSearchEventsLink, router, search]
	);

	const keyDown = (event: KeyboardEvent) => {
		if (event.key === 'Enter') {
			onSearchHandler(event);
		}
	};

	const styles = useStyles(heroScreenSearchBarStyles);

	const mediaSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	return (
		<Box sx={styles.root} onKeyDown={keyDown}>
			<Box sx={styles.inputSection}>
				<TextField
					sx={styles.textField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start" sx={styles.inputAdornment}>
								<ImageWrapper sx={styles.textFieldIcon}>
									<Image src={searchIcon} alt="search" />
								</ImageWrapper>
							</InputAdornment>
						),
					}}
					variant="standard"
					placeholder={mediaSm ? 'Search events' : 'Search communities or events'}
					onChange={handleSearchChange}
					value={search}
				/>
			</Box>
			<Box sx={styles.separator} />
			<Box sx={styles.selectSection}>
				<FormControl fullWidth variant="filled" sx={styles.selectFormControll}>
					<InputLabel sx={{ ...styles.selectLabel, display: { xs: 'none', sm: 'block' } }} id="search-bar-select-label">
						Select your city
					</InputLabel>
					<InputLabel sx={{ ...styles.selectLabel, display: { xs: 'block', sm: 'none' } }} id="search-bar-select-label">
						City
					</InputLabel>
					<Select
						labelId="search-bar-select-label"
						label="Select your city"
						value={locationCityId}
						onChange={handleLocationChange}
					>
						{locations.map(location => (
							<MenuItem sx={styles.selectItem} key={location.id} value={location.id}>
								{location.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
		</Box>
	);
};

export default HeroScreenSearchBar;
